/**
 * GameChanger lineup card PDF parser.
 *
 * GC lineup cards are a single portrait page with a predictable text layout:
 *   - Header rows: team name, opponent, address, date
 *   - Column header row: PLAYER | # | POS. | SUBS | # | POS.
 *   - Data rows: slot# · player name · jersey# · position  (left side)
 *                         sub name? · jersey# · position  (right side, optional)
 *
 * Each data row groups by y-coordinate. The slot number (1–12) is the leftmost
 * integer, the jersey number is a 1–3 digit number in the middle column region,
 * and the position token (P, C, 1B, 2B, etc.) is to the right of the jersey.
 *
 * We match parsed entries against the Dugout roster by jersey number first,
 * then by last-name fuzzy match as a fallback.
 */

import { PdfPage, PdfTextItem, groupIntoRows } from './types';

export interface LineupCardEntry {
  slot: number;           // batting order slot 1–12
  name: string;           // as printed on the card
  jerseyNumber: string;   // as printed
  position: string;       // starting position
  playerId?: string;      // matched Dugout player id
}

export interface ParsedLineupCard {
  teamName?: string;
  opponent?: string;
  date?: string;
  entries: LineupCardEntry[];
  unmatched: LineupCardEntry[];  // entries where no roster player was found
}

const POSITION_TOKENS = new Set([
  'P','C','1B','2B','3B','SS','LF','CF','RF','EH','DH','DP','FLEX','BN','BENCH','LCF','RCF'
]);

function isSlot(s: string): boolean { return /^([1-9]|1[0-2])$/.test(s.trim()); }
function isJersey(s: string): boolean { return /^\d{1,3}$/.test(s.trim()); }
function isPosition(s: string): boolean { return POSITION_TOKENS.has(s.trim().toUpperCase()); }

/** Normalize a name for fuzzy comparison — lowercase, no punctuation. */
function normName(s: string): string { return s.toLowerCase().replace(/[^a-z0-9 ]/g, '').trim(); }

/** True when name A contains name B or vice-versa (handles "Graham Jacobsen" vs "Jacobsen"). */
function nameMatches(a: string, b: string): boolean {
  const na = normName(a), nb = normName(b);
  return na === nb || na.includes(nb) || nb.includes(na);
}

/**
 * Parse a GC lineup card PDF (single page) into structured entries.
 * Pass `players` to attempt roster matching by jersey number + name.
 */
export function parseLineupCard(pages: PdfPage[], players: any[]): ParsedLineupCard {
  const page = pages[0];
  if (!page) return { entries: [], unmatched: [] };

  const rows = groupIntoRows(page.items, 4);

  let teamName: string | undefined;
  let opponent: string | undefined;
  let date: string | undefined;

  // ── Header extraction ─────────────────────────────────────────────────────
  for (const row of rows.slice(0, 10)) {
    const text = row.map(i => i.str).join(' ').trim();
    // Date line: "Thursday, June 18, 2026" or similar
    if (/\b(january|february|march|april|may|june|july|august|september|october|november|december)\b/i.test(text) && /\d{4}/.test(text)) {
      date = text;
      continue;
    }
    // "vs." or "@" opponent line
    if (/\bvs\.?\b/i.test(text) || /^@/.test(text)) {
      const m = text.match(/(?:vs\.?\s*|@\s*)(.+)/i);
      if (m) opponent = m[1].replace(/\s+\d{4}.*/, '').trim(); // strip year suffix
      continue;
    }
    // Team name — first substantial row that isn't a date/vs line
    if (!teamName && text.length > 2 && !/^\d/.test(text) && !/PLAYER|POS|SUBS/i.test(text)) {
      teamName = text;
    }
  }

  // ── Find the column header row to calibrate x positions ──────────────────
  let playerColX = 0;
  let jerseyColX = 0;
  let posColX = 0;

  for (const row of rows) {
    const texts = row.map(i => i.str.trim().toUpperCase());
    if (texts.includes('PLAYER') && (texts.includes('#') || texts.includes('POS') || texts.includes('POS.'))) {
      // Calibrate: PLAYER header x → name column, # → jersey col, POS → position col
      for (const item of row) {
        const t = item.str.trim().toUpperCase();
        if (t === 'PLAYER') playerColX = item.x;
        if (t === '#' && item.x > playerColX + 30 && jerseyColX === 0) jerseyColX = item.x;
        if ((t === 'POS' || t === 'POS.') && posColX === 0) posColX = item.x;
      }
      break;
    }
  }

  // Fallback column calibration if header not found: use page width fractions
  if (jerseyColX === 0) jerseyColX = page.width * 0.55;
  if (posColX === 0) posColX = page.width * 0.65;

  // ── Parse data rows ───────────────────────────────────────────────────────
  const entries: LineupCardEntry[] = [];

  for (const row of rows) {
    const items = row.filter(i => i.str.trim().length > 0);
    if (items.length < 3) continue;

    // First token must be a batting-order slot number
    const firstStr = items[0].str.trim();
    if (!isSlot(firstStr)) continue;
    const slot = parseInt(firstStr);

    // Gather remaining tokens from the LEFT side of the page only
    // (right side has SUBS columns which we ignore)
    const leftItems = items.filter(i => i.x < page.width * 0.6);

    // Find jersey number: a short integer in the jersey column region
    const jerseyItem = leftItems.find(i =>
      isJersey(i.str.trim()) &&
      i.str.trim() !== firstStr &&
      Math.abs(i.x - jerseyColX) < page.width * 0.15
    );
    if (!jerseyItem) continue;
    const jerseyNumber = jerseyItem.str.trim();

    // Find position: token matching known positions after the jersey column
    const posItem = leftItems.find(i =>
      isPosition(i.str.trim()) &&
      i.x > jerseyItem.x
    );
    const position = posItem ? posItem.str.trim().toUpperCase() : '';

    // Name: all left-side text tokens between the slot# and the jersey#, joined
    const nameItems = leftItems.filter(i =>
      i.x > items[0].x + 5 &&       // after slot number
      i.x < jerseyItem.x - 2 &&     // before jersey number
      !isSlot(i.str.trim()) &&
      !isJersey(i.str.trim()) &&
      !isPosition(i.str.trim())
    );
    const name = nameItems.map(i => i.str.trim()).join(' ').trim();
    if (!name) continue;

    entries.push({ slot, name, jerseyNumber, position });
  }

  entries.sort((a, b) => a.slot - b.slot);

  // ── Roster matching ───────────────────────────────────────────────────────
  const matched: LineupCardEntry[] = [];
  const unmatched: LineupCardEntry[] = [];

  for (const entry of entries) {
    // 1. Exact jersey match
    let player = players.find((p: any) => p.number === entry.jerseyNumber);

    // 2. Jersey match with name confirmation (avoids wrong player if two share a number)
    if (player && !nameMatches(player.name, entry.name)) {
      const byName = players.find((p: any) => nameMatches(p.name, entry.name));
      if (byName) player = byName;
    }

    // 3. Name-only fallback
    if (!player) {
      player = players.find((p: any) => nameMatches(p.name, entry.name));
    }

    if (player) {
      matched.push({ ...entry, playerId: player.id });
    } else {
      unmatched.push(entry);
    }
  }

  return { teamName, opponent, date, entries: matched, unmatched };
}
